import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import * as S from './InvoicePromotionDetails.styles';
import type { InvoicePromotion } from '@/features/Profile/sections/Packages/api';
import type { PromotionPackage } from '@/features/Profile/sections/Packages/PackagesSection.helpers';
import { ROUTES } from '@/constants/routes';
import { formatNumber, makeHrefForPhone } from '@/utils/helpers';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

const SALES_CONTACTS = [
  {
    nameKey: 'pages.cabinet.invoice_promotion_sales_manager_name_1',
    phone: '051 290 21 47',
    roleKey: 'pages.cabinet.invoice_promotion_sales_manager',
  },
  {
    nameKey: 'pages.cabinet.invoice_promotion_sales_manager_name_2',
    phone: '010 256 99 06',
    roleKey: 'pages.cabinet.invoice_promotion_sales_manager',
  },
] as const;

const PACKAGE_SKELETON_COUNT = 4;
const PACKAGE_CARD_SKELETON_HEIGHT = 400;
const PACKAGE_SKELETON_KEYS = Array.from(
  { length: PACKAGE_SKELETON_COUNT },
  (_, index) => index
);

type InvoicePromotionDetailsProps = {
  isPackagesLoading: boolean;
  onNavigate: () => void;
  packages: PromotionPackage[];
  promotion: InvoicePromotion;
};

export function InvoicePromotionDetails({
  isPackagesLoading,
  onNavigate,
  packages,
  promotion,
}: InvoicePromotionDetailsProps) {
  const { t } = useTranslation();

  return (
    <S.DetailsContent>
      {promotion.description && (
        <S.ModalDescription>{promotion.description}</S.ModalDescription>
      )}

      {(isPackagesLoading || packages.length > 0) && (
        <S.PackagesSection>
          <S.PackagesHeader>
            <S.SectionTitle>
              {t('pages.cabinet.invoice_promotion_discount_table')}
            </S.SectionTitle>
            <S.BusinessLink href={ROUTES.businessPackages} onClick={onNavigate}>
              {t('pages.cabinet.invoice_promotion_go_to_business')}
            </S.BusinessLink>
          </S.PackagesHeader>
          <S.PackageCards>
            {isPackagesLoading
              ? PACKAGE_SKELETON_KEYS.map(index => (
                  <SkeletonLoader
                    key={index}
                    aria-hidden="true"
                    appearance="primary"
                    borderRadius={21}
                    dataTestId="package-card-skeleton"
                    height={PACKAGE_CARD_SKELETON_HEIGHT}
                  />
                ))
              : packages.map(item => (
                  <S.PackageCard key={item.name}>
                    <S.PackageName>
                      {t('pages.cabinet.package_name', { name: item.name })}
                    </S.PackageName>
                    <S.PackagePeriods>
                      {item.periods.map(period => (
                        <S.PackagePeriod key={period.days}>
                          <S.PeriodDays>
                            {t('pages.cabinet.package_period_days', {
                              days: period.days,
                            })}
                          </S.PeriodDays>
                          {period.discountPercent !== null && (
                            <S.OriginalPriceRow>
                              <S.OriginalPrice>
                                {formatNumber(period.originalPrice)} ₼
                              </S.OriginalPrice>
                              <S.DiscountBadge>
                                -
                                {formatNumber(period.discountPercent, 1, true, {
                                  decimalSeparator: '.',
                                })}
                                %
                              </S.DiscountBadge>
                            </S.OriginalPriceRow>
                          )}
                          <S.PromotionPrice>
                            {formatNumber(period.price)} ₼
                          </S.PromotionPrice>
                        </S.PackagePeriod>
                      ))}
                    </S.PackagePeriods>
                    <S.ConnectButton
                      forwardedAs={Link}
                      href={ROUTES.businessPackages}
                      onClick={onNavigate}
                    >
                      {t('pages.cabinet.invoice_promotion_connect')}
                    </S.ConnectButton>
                  </S.PackageCard>
                ))}
          </S.PackageCards>
        </S.PackagesSection>
      )}

      <S.ContactsSection>
        <S.SectionTitle>
          {t('pages.cabinet.invoice_promotion_additional_info')}
        </S.SectionTitle>
        <S.ContactsContent>
          <S.ContactPeople>
            {SALES_CONTACTS.map(contact => (
              <S.Contact key={contact.phone}>
                <S.ContactRole>{t(contact.roleKey)}</S.ContactRole>
                <S.ContactNameRow>
                  <S.ContactName>{t(contact.nameKey)}</S.ContactName>
                  <span>—</span>
                  <S.ContactLink href={makeHrefForPhone(contact.phone)}>
                    {contact.phone}
                  </S.ContactLink>
                </S.ContactNameRow>
              </S.Contact>
            ))}
          </S.ContactPeople>
          <S.EmailContacts>
            <S.MailIcon aria-hidden="true" src="/images/mail-dark.svg" />
            <S.ContactLink href="mailto:sales@digit.az">
              sales@digit.az
            </S.ContactLink>
            <S.EmailDivider aria-hidden="true" />
            <S.ContactLink href="mailto:info@digit.az">
              info@digit.az
            </S.ContactLink>
          </S.EmailContacts>
        </S.ContactsContent>
      </S.ContactsSection>
    </S.DetailsContent>
  );
}
